import { useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import {
  Alert, Button, Card, CardBody, CardHeader, Input, Select, Textarea,
} from '../../components/ui'
import { PlusIcon } from '../../components/icons'

interface DraftModule { module_name: string; module_description: string; content: string }
interface DraftQuestion { text: string; options: [string, string, string, string]; correct: number }

const LEVELS = ['foundation', 'practitioner', 'expert']
const TARGET_ROLES = [
  ['miner', 'Miner'], ['team_leader', 'Team Leader'], ['gov_inspector', 'Government Inspector'],
  ['gov_regional', 'Government Regional Officer'], ['gov_director', 'Government Director'], ['all', 'All Roles'],
]

export default function CourseUploadCreate() {
  const navigate = useNavigate()
  const [modules, setModules] = useState<DraftModule[]>([{ module_name: '', module_description: '', content: '' }])
  const [questions, setQuestions] = useState<DraftQuestion[]>([])

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<{ id: string }>('/gov/course-uploads', body),
    onSuccess: (res) => navigate(`/course-uploads?highlight=${res.id}`),
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    create.mutate({
      course_name: String(form.get('course_name') ?? '').trim(),
      course_description: String(form.get('course_description') ?? '').trim(),
      estimated_duration: String(form.get('estimated_duration') ?? '').trim(),
      level: String(form.get('level') ?? 'foundation'),
      target_role: String(form.get('target_role') ?? ''),
      learning_outcomes: String(form.get('learning_outcomes') ?? '').split('\n').map((s) => s.trim()).filter(Boolean),
      modules: modules.filter((m) => m.module_name.trim()),
      final_exam: {
        pass_mark: Number(form.get('pass_mark') ?? 70),
        questions: questions.filter((q) => q.text.trim()),
      },
    })
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>New course draft</h1>
          <p className="page-head__sub">
            Saved as a draft. Submit it for review once ready — a different admin must approve it before it goes live.
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit}>
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Course details" />
          <CardBody>
            <div className="stack" style={{ gap: 'var(--sp-4)' }}>
              {create.isError && (
                <Alert tone="danger">
                  {create.error instanceof ApiError ? create.error.message : 'Could not save that draft.'}
                </Alert>
              )}
              <Input label="Course name" name="course_name" required />
              <Textarea label="Description" name="course_description" rows={3} required />
              <div className="grid grid--2">
                <Select label="Level" name="level" defaultValue="foundation">
                  {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </Select>
                <Select label="Target role" name="target_role" defaultValue="all">
                  {TARGET_ROLES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                </Select>
              </div>
              <Input label="Estimated duration" name="estimated_duration" placeholder="e.g. 4 hours" />
              <Textarea label="Learning outcomes (one per line)" name="learning_outcomes" rows={3} />
            </div>
          </CardBody>
        </Card>

        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Modules" sub="Each module is a reading section learners complete in order." />
          <CardBody>
            <div className="stack" style={{ gap: 'var(--sp-5)' }}>
              {modules.map((m, i) => (
                <div key={i} className="stack" style={{ gap: 'var(--sp-3)', paddingBottom: 'var(--sp-3)', borderBottom: '1px solid var(--border)' }}>
                  <Input
                    label={`Module ${i + 1} name`}
                    value={m.module_name}
                    onChange={(e) => setModules((cur) => cur.map((x, xi) => (xi === i ? { ...x, module_name: e.target.value } : x)))}
                  />
                  <Textarea
                    label="Content"
                    rows={3}
                    value={m.content}
                    onChange={(e) => setModules((cur) => cur.map((x, xi) => (xi === i ? { ...x, content: e.target.value } : x)))}
                  />
                </div>
              ))}
              <Button type="button" size="sm" variant="ghost" icon={<PlusIcon size={14} />}
                      onClick={() => setModules((cur) => [...cur, { module_name: '', module_description: '', content: '' }])}>
                Add module
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Final assessment" sub="Multiple-choice questions taken after all modules are complete." />
          <CardBody>
            <div className="stack" style={{ gap: 'var(--sp-5)' }}>
              <Input label="Pass mark (%)" name="pass_mark" type="number" min="0" max="100" defaultValue={70} style={{ maxWidth: 160 }} />
              {questions.map((q, i) => (
                <div key={i} className="stack" style={{ gap: 'var(--sp-2)', paddingBottom: 'var(--sp-3)', borderBottom: '1px solid var(--border)' }}>
                  <Input
                    label={`Question ${i + 1}`}
                    value={q.text}
                    onChange={(e) => setQuestions((cur) => cur.map((x, xi) => (xi === i ? { ...x, text: e.target.value } : x)))}
                  />
                  {q.options.map((opt, oi) => (
                    <div key={oi} className="row" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
                      <input type="radio" name={`correct-${i}`} checked={q.correct === oi}
                             onChange={() => setQuestions((cur) => cur.map((x, xi) => (xi === i ? { ...x, correct: oi } : x)))} />
                      <div style={{ flex: 1 }}>
                        <Input
                          placeholder={`Option ${oi + 1}`}
                          value={opt}
                          onChange={(e) => setQuestions((cur) => cur.map((x, xi) => (
                            xi === i ? { ...x, options: x.options.map((o, oii) => (oii === oi ? e.target.value : o)) as DraftQuestion['options'] } : x
                          )))}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              <Button type="button" size="sm" variant="ghost" icon={<PlusIcon size={14} />}
                      onClick={() => setQuestions((cur) => [...cur, { text: '', options: ['', '', '', ''], correct: 0 }])}>
                Add question
              </Button>
            </div>
          </CardBody>
        </Card>

        <div className="row">
          <Button type="submit" loading={create.isPending}>Save draft</Button>
        </div>
      </form>
    </>
  )
}
